/**
 * formatIsoDate — format an IS date string for display.
 *
 * Shared by the profile sections that render an IS `YYYY-MM-DD` date
 * (MembershipCertificateSection, MembershipSection). Parses as a LOCAL-calendar
 * date (never UTC, RD-007) so the day never shifts across the timezone offset;
 * accepts a bare `YYYY-MM-DD` or a full ISO timestamp (splits on 'T'). Falls back
 * to the raw string rather than "Invalid Date" if the port ever sends a malformed
 * value.
 */
export function formatIsoDate(dateString: string, locale: string): string {
  const datePart = dateString.split('T')[0] ?? dateString;
  const parts = datePart.split('-');
  const [y, m, d] = parts.map(Number) as [number, number, number];
  // Require exactly three numeric parts: a partial value like '2021-05' yields an
  // `undefined` day that slips past `Number.isNaN` (which is false for undefined),
  // so guard the length before constructing the Date to honour the raw-string fallback.
  if (parts.length !== 3 || Number.isNaN(y) || Number.isNaN(m) || Number.isNaN(d)) {
    return dateString;
  }
  const date = new Date(y, m - 1, d);
  // Reject numeric-but-out-of-range values ('2021-13-45', '2021-02-30'): the Date
  // constructor rolls these forward to a valid-but-WRONG day, so round-trip-verify the
  // components and fall back to the raw string rather than show a plausible wrong date.
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) {
    return dateString;
  }
  const formatter = new Intl.DateTimeFormat(locale === 'en' ? 'en-GB' : 'fi-FI', {
    day: 'numeric',
    month: locale === 'en' ? 'long' : 'numeric',
    year: 'numeric',
  });
  return formatter.format(date);
}
